import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, ElementRef, inject, input, NgZone, output, Renderer2, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatChip, MatChipListbox } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { EnpsComponent } from '@common/components/base/enps-component';
import { AttachmentListItem } from '@common/data-models/complex-types/attachment-list';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { LoaderComponent } from '@loader/loader';
import { Store } from '@ngrx/store';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { MediaTagTypes } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { selectTopicsWithAssignmentsAndStories } from '@src/suite/common/store/records/records.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { MirAttachment, MosObjectType, mosTypeToIcon } from '@src/suite/common/utils/mos-utlis';
import { isStoryInRundown, isStoryLoose, recordToSendForAttachmentPool } from '@src/suite/common/utils/record-utils';
import { AppService } from '@suite/app.service';
import { AttachmentService } from '@suite/common/services/attachment-service';
import { BasicContentService } from '@suite/common/services/basic-content-service';
import { NgxUploaderModule, UploaderOptions, UploadFile, UploadOutput } from 'ngx-uploader';

import { ExternalActionsOverlayComponent } from '../../../playbook/components/external-actions-overlay/external-actions-overlay';
import { AttachmentsListItemComponent } from './attachments-list-item/attachments-list-item';

declare let window;

@Component({
    selector: 'st-attachments-list',
    imports: [AttachmentsListItemComponent, ExternalActionsOverlayComponent, MatChip, MatChipListbox, MatIcon, MatTooltip, NgOptimizedImage, NgxUploaderModule],
    templateUrl: './attachments-list.html',
    styleUrl: './attachments-list.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-attachments-list',
    },
})
export class AttachmentsListComponent extends EnpsComponent {
    MediaTagTypes = MediaTagTypes;
    store = inject(Store);
    appService = inject(AppService);
    ngZone = inject(NgZone);
    renderer = inject(Renderer2);
    nomWebServicesJson = inject(NomWebServicesJson);
    visualStateService = inject(VisualStateService);
    globalTablesService = inject(GlobalTablesService);
    basicContentService = inject(BasicContentService);
    attachmentService = inject(AttachmentService);
    changeDetectorRef = inject(ChangeDetectorRef);
    modalService = inject(ModalService);

    // inputs
    readonly record = input<StorytellingAssignmentRecord | StorytellingStoryRecord | StorytellingTopicRecord | undefined>({});
    readonly recordComputed = computed(() => this.record(), { equal: (a, b) => a?.RowModTime?.valueOf() === b?.RowModTime?.valueOf() });
    readonly loader = input<LoaderComponent>();
    readonly disabled = input(false, {
        transform: coerceBooleanProperty,
    });

    // outputs
    readonly linkedRecordOpen = output<StorytellingRecord>();

    // store signals
    languageSettings = this.store.selectSignal(selectI18N)();

    // signals
    readonly recordAttachments = signal<UploadFile[]>([]);
    readonly options = signal<UploaderOptions>({
        concurrency: 1,
        maxFileSize: this.attachmentService.maxExternalDocumentSize,
    });

    readonly isAndroidWrapper = signal<boolean>(null);
    readonly dragOver = signal<boolean>(null);
    private unbindNAUListener: () => void = null;

    readonly topicsWithAssignmentsAndStoriesComp = computed(() => {
        return this.store.selectSignal(selectTopicsWithAssignmentsAndStories)();
    });

    readonly input = viewChild<ElementRef<HTMLInputElement>>('file');

    readonly linkedRecords = computed(() => {
        return this.record()?.LinkedRecords || [];
    });
    readonly editable = computed(() => {
        return this.globalTablesService.securityPrivileges[this.nomWebServicesJson.PSTGroup].topic.write && !this.disabled();
    });
    readonly attachmentPoolItems = computed(() => {
        switch (this.record()?.Type) {
            case ObjectTypes.TOPIC:
                return this.record()?.attachments || [];
            case ObjectTypes.ASSIGNMENT:
            case ObjectTypes.SCRIPT: {
                if (this.record()?.Type === ObjectTypes.SCRIPT && (isStoryLoose(this.record()) || isStoryInRundown(this.record()))) {
                    return this.record()?.attachments || [];
                }
                const casted = this.record() as StorytellingAssignmentRecord | StorytellingStoryRecord;
                return this.topicsWithAssignmentsAndStoriesComp()?.get(casted?.topicMetadata?.Guid)?.attachments || [];
            }
        }
    });

    readonly attachmentPoolItems1 = computed(() => {
        switch (this.record()?.Type) {
            case ObjectTypes.TOPIC:
                return this.record()?.AttachmentPool || [];
            case ObjectTypes.ASSIGNMENT:
            case ObjectTypes.SCRIPT: {
                if (this.record()?.Type === ObjectTypes.SCRIPT && (isStoryLoose(this.record()) || isStoryInRundown(this.record()))) {
                    return this.record()?.AttachmentPool || [];
                }
                const casted = this.record() as StorytellingAssignmentRecord | StorytellingStoryRecord;
                return this.topicsWithAssignmentsAndStoriesComp()?.get(casted?.topicMetadata?.Guid)?.AttachmentPool || [];
            }
        }

        return [];
    });
    readonly groupedAttachmentPoolItems = computed(() => {
        const items = this.attachmentPoolItems();
        const items1 = this.attachmentPoolItems1();
        // remove duplicate records from attachments and AttachmentPool by their unique identifier
        const allItems = Array.from(new Map([...items, ...items1].filter(item => item).map(item => [item.guid || item.Guid, item])).values());
        const groupedItems = allItems.reduce(
            (acc, item) => {
                if (item?.objType) {
                    switch (item?.objType) {
                        case MosObjectType.STILL:
                            acc.stills.items.push({ ...item, attachmentType: 'mirAttachment' });
                            break;
                        case MosObjectType.AUDIO:
                            acc.audios.items.push({ ...item, attachmentType: 'mirAttachment' });
                            break;
                        case MosObjectType.VIDEO:
                            acc.videos.items.push({ ...item, attachmentType: 'mirAttachment' });
                            break;
                        default:
                            acc.unknown.items.push({ ...item, attachmentType: 'mirAttachment' });
                            break;
                    }
                } else if (item?.TagType) {
                    switch (item?.TagType) {
                        case MediaTagTypes.IMAGE:
                            acc.stills.items.push({ ...item, attachmentType: 'attachmentPoolItem' });
                            break;
                        case MediaTagTypes.AUDIO:
                            acc.audios.items.push({ ...item, attachmentType: 'attachmentPoolItem' });
                            break;
                        case MediaTagTypes.VIDEO:
                            acc.videos.items.push({ ...item, attachmentType: 'attachmentPoolItem' });
                            break;
                        default:
                            acc.unknown.items.push({ ...item, attachmentType: 'attachmentPoolItem' });
                            break;
                    }
                }

                return acc;
            },
            {
                stills: {
                    id: 'stills',
                    items: [] as MirAttachment[],
                    label: this.languageSettings[I18nKeys.PSTWebAttachmentsListStill],
                    icon: mosTypeToIcon(MosObjectType.STILL),
                },
                audios: {
                    id: 'audios',
                    items: [] as MirAttachment[],
                    label: this.languageSettings[I18nKeys.PSTWebAttachmentsListAudio],
                    icon: mosTypeToIcon(MosObjectType.AUDIO),
                },
                videos: {
                    id: 'videos',
                    items: [] as MirAttachment[],
                    label: this.languageSettings[I18nKeys.PSTWebAttachmentsListVideo],
                    icon: mosTypeToIcon(MosObjectType.VIDEO),
                },
                unknown: {
                    id: 'unknown',
                    items: [] as MirAttachment[],
                    label: this.languageSettings[I18nKeys.PSTWebAttachmentsListSummary],
                    icon: mosTypeToIcon(MosObjectType.SUMMARY),
                },
            }
        );
        return Object.values(groupedItems);
    });
    readonly mediaItems = computed(() => {
        return this.attachmentPoolItems().reduce(
            (acc, item) => {
                if (item.url?.trim()) {
                    acc.names.push(item.slugText);
                    acc.urls.push(item.url);
                }
                return acc;
            },
            { names: [] as string[], urls: [] as string[] }
        );
    });

    constructor() {
        super();
        this.isAndroidWrapper.set(this.visualStateService.checkIfAdroidWrapper());
    }

    async removeAttachment(attachment: AttachmentListItem) {
        this.store.dispatch(RecordHttpActions.removeAttachment({ attachment, record: recordToSendForAttachmentPool(this.record()) }));
    }

    browse(): void {
        if (this.record()?.isNewRecord) {
            this.showWarningMessage();
        } else {
            this.input().nativeElement.click();
        }
    }

    onUploadOutput(upload: UploadOutput) {
        const uploadFile = () => {
            if (this.record()?.isNewRecord) {
                this.showWarningMessage();
            } else {
                // TODO: ugly ugly
                this.store.dispatch(RecordHttpActions.shouldProceedAttachment({ recordAttachments: this.recordAttachments(), record: recordToSendForAttachmentPool(this.record()) }));
                this.recordAttachments.set([]);
            }
        };
        switch (upload.type) {
            case 'allAddedToQueue':
                uploadFile();
                break;
            case 'addedToQueue':
                if (typeof upload.file !== 'undefined') {
                    this.recordAttachments.update(value => {
                        return [...(value || []), upload.file];
                    });
                }
                break;
            case 'dragOver':
                this.dragOver.set(true);
                break;
            case 'dragOut':
            case 'drop':
                this.dragOver.set(false);
                break;
        }
        this.input().nativeElement.value = '';
    }

    showWarningMessage() {
        this.modalService.openAlert({ title: this.languageSettings[I18nKeys.PSTWebNewRecordFileUploadWarningTitle], content: this.languageSettings[I18nKeys.PSTWebNewRecordFileUploadWarningContent] });
    }

    async nativeAndroidUpload(event) {
        if (this.unbindNAUListener) this.unbindNAUListener();
        this.store.dispatch(RecordHttpActions.nativeAndroidUpload({ nativeAndroidEvent: event, record: this.record() }));
    }

    fireUploadEvent(event: Event) {
        if (this.isAndroidWrapper()) {
            window.globalAttachmentForm = null;
            if (this.input() && this.input().nativeElement) {
                window.globalAttachmentForm = this.input().nativeElement;
            }

            if (window.globalAttachmentForm && window.ANDROID_WRAPPER && window.ANDROID_WRAPPER.openFileChooser) {
                this.ngZone.runOutsideAngular(() => {
                    if (this.input()) {
                        if (this.unbindNAUListener) this.unbindNAUListener();
                        this.unbindNAUListener = this.renderer.listen(this.input().nativeElement, 'nativeAndroidFileUpload', this.nativeAndroidUpload.bind(this));
                    }
                });
                window.ANDROID_WRAPPER.openFileChooser();
            }
            event.preventDefault();
            event.stopPropagation();
            return false;
        }
    }

    // left for future use
    async downloadAttachment(_attachment: AttachmentListItem) {
        if (window.ANDROID_WRAPPER) {
            this.loader().show();
            this.loader().label.set(this.languageSettings.PSTWebDownloading);
        }
        //await this.appService.attachmentService.downloadAttachment(attachment.database, attachment.path, attachment.guid, attachment.title);
        if (window.ANDROID_WRAPPER) {
            this.loader().hide();
            this.loader().label.set('');
        }
    }

    openMIRAttachment(attachment: MirAttachment) {
        if (!attachment.url?.trim()) {
            return;
        }
        const { urls } = this.mediaItems();
        if (urls.length > 0) {
            this.store.dispatch(OpenFullscreenModalActions.setFullscreenMediaUrls({ mediaUrls: urls, currentMediaUrl: attachment.url, mediaNames: [] }));
        }
    }

    openAttachment(attachment: MirAttachment) {
        const urls = this.attachmentPoolItems1().map(item => item.Url);
        const names = this.attachmentPoolItems1().map(item => item.Name);
        this.store.dispatch(OpenFullscreenModalActions.setFullscreenMediaUrls({ mediaUrls: urls, currentMediaUrl: attachment.Url, mediaNames: names }));
    }

    dragStart(event: DragEvent, attachmentItem: MirAttachment) {
        const _dragData = `<mos>
                            <ncsItem>
                                <item>
                                    <itemID>0</itemID>
                                    <itemSlug>${attachmentItem.Name}</itemSlug>
                                    <objID>${attachmentItem.Guid}</objID>
                                    <mosID>AttachmentPool</mosID>
                                    <mosAbstract>${attachmentItem.Name}</mosAbstract>
                                    <objPaths>
                                        <objPath techDescription="${attachmentItem.Type}">${attachmentItem.Url}</objPath>
                                        <objProxyPath techDescription="${attachmentItem.Type}">${attachmentItem.Url}</objProxyPath>
                                    </objPaths>
                                </item>
                            </ncsItem>
                        </mos>`;
        const dragData = {
            type: 'AttachmentPool',
            data: _dragData,
        };
        event.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        event.dataTransfer.effectAllowed = 'copyMove';
        event.stopPropagation();
    }

    openLinkedRecord(item) {
        this.linkedRecordOpen.emit(item);
    }

    attachmentTrackBy(item: MirAttachment) {
        return item.guid || item.Guid;
    }
}
